<template>
    <modal size="lg" @close="$emit('close')" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title" v-if="step === 'check-options'">{{ createSeparateCheck ? 'Add Separate Check' : 'Modify Earnings' }}</h3>
            <h3 class="modal-title" v-else-if="step === 'earnings'">{{ createSeparateCheck ? 'Add Separate Check' : 'Modify Earnings' }}</h3>
            <h3 class="modal-title" v-else-if="step === 'deductions-taxes'">Deductions &amp; Taxes</h3>
            <h3 class="modal-title" v-else-if="step === 'earnings-statement'">Earnings Statement</h3>
        </template>

        <template v-slot:subheading>
            <div v-if="employee && step != 'earnings-statement'">
                <div class="p-1 unpad-x bg-150 rounded">
                    <div><span class="text-muted">Employee: </span>{{ employee.full_name }}</div>
                    <div><span class="text-muted">Company: </span>{{ employee.company.name }}</div>
                </div>
            </div>
        </template>

        <slot>
            <template v-if="dataLoaded">
                <!-- Separate-check step: how amounts are entered and how the check is delivered -->
                <div v-if="step === 'check-options'">
                    <form-input
                        label="Entry Type"
                        v-model="formData.entry_type"
                        type="select"
                        :options="entryTypeOptions"
                        :noblank="true"
                    />
                    <div class="field-helper-text mb-2">How will you enter the amounts for this check?</div>

                    <form-input
                        label="Check Delivery"
                        v-model="formData.check_delivery"
                        type="select"
                        :options="checkDeliveryOptions"
                        :noblank="true"
                    />
                    <div class="field-helper-text mb-2">How will this employee receive these funds?</div>
                </div>

                <!-- Step 1: Earnings -->
                <div v-show="step === 'earnings'">
                    <form-input
                        v-if="createSeparateCheck"
                        label="Position"
                        :model-value="selectedPositionId"
                        @update:model-value="selectPosition($event)"
                        type="select"
                        :options="positionSelectOptions"
                        class="pb-2"
                    />
                    <div v-for="paycheck in localPaychecks" :key="paycheck.id || 'new'">
                        <div v-for="group in positionGroups(paycheck)" :key="group.slug" class="mb-1 position edit-mode">
                            <div v-if="!createSeparateCheck" class="pb-1"><strong>Position: {{ paycheck.work_location.name }} / {{ group.slug }}</strong></div>
                            <table class="table css-table widget-table table-fixed">
                                <thead>
                                    <tr>
                                        <th class="pl-0 earning-description">Earning</th>
                                        <th class="text-left">Rate</th>
                                        <th class="text-right">Hours/Units</th>
                                        <th class="text-right">Overtime <i class="fa fa-info-circle"></i></th>
                                        <th class="text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody v-for="(earning, idx) in group.earnings" :key="idx" class="earning">
                                    <tr>
                                        <td v-if="earning.is_custom" class="pl-0">
                                            <form-input
                                                :model-value="earningCompanyTypeValue(paycheck, earning)"
                                                @update:model-value="onCustomEarningTypeChange(earning, $event)"
                                                type="select"
                                                :options="customEarningTypeOptions(paycheck.company_id)"
                                                :deletable="true"
                                                @delete="removeEarning(paycheck, earning)"
                                                class="custom-earning-select"
                                            />
                                        </td>
                                        <td v-else class="truncate-wrapper pl-0 align-middle">{{ earning.earning_type_name }}</td>
                                        <td class="text-left"><cell-input class="grid-input" type="currency-input" :value="earning.rate" :disabled="!earning.is_custom || (earning.earning_type_type !== 'hourly' && earning.earning_type_type !== 'per-unit')" @blur="onCellBlur(earning, 'rate', $event)" /></td>
                                        <td class="text-right" v-if="earning.earning_type_type == 'hourly'"><cell-input class="grid-input" type="decimal-input" :value="earning.hours" :show-zero="hasEnteredValue(earning.hours)" @blur="onCellBlur(earning, 'hours', $event)" /></td>
                                        <td class="text-right" v-else-if="earning.earning_type_type == 'per-unit'"><cell-input class="grid-input" type="decimal-input" :value="earning.units" :show-zero="hasEnteredValue(earning.units)" @blur="onCellBlur(earning, 'units', $event)" /></td>
                                        <td class="text-right" v-else><cell-input class="grid-input" type="decimal-input" :value="''" :disabled="true" /></td>
                                        <td class="text-right"><cell-input class="grid-input" type="decimal-input" :value="earning.overtime_hours" :show-zero="hasEnteredValue(earning.overtime_hours)" :disabled="earning.earning_type_type !== 'hourly'" @blur="onCellBlur(earning, 'overtime_hours', $event)" /></td>
                                        <td class="text-left"><cell-input class="grid-input" type="currency-input" :value="earning.amount" :show-zero="Boolean(earning.is_amount_entered)" :disabled="earning.earning_type_type !== 'amount'" @blur="onCellBlur(earning, 'amount', $event)" /></td>
                                    </tr>
                                </tbody>
                            </table>
                            <button class="btn btn-link" @click.prevent="addCustomEarning(paycheck, group.slug)">+ Add Earning</button>
                        </div>
                    </div>

                    <!-- Supplemental rate: shown when at least one earning is eligible. Every eligible
                    earning is listed with its own Yes/No toggle floated to the right; a single eligible
                    earning shows the naming prompt inline instead of a separate label. -->
                    <div v-if="eligibleSupplementalEarnings.length" class="p-2 bg-150 rounded mt-1">
                        <div class="d-flex align-items-center">
                            <strong>Supplemental Rate</strong>
                            <more-info style="margin-top: 0;">
                                <p class="mb-0">
                                    Supplemental wages such as bonuses or commissions can have income tax withheld
                                    at a flat supplemental rate instead of the employee's regular withholding rate.
                                </p>
                            </more-info>
                        </div>
                        <div v-if="eligibleSupplementalEarnings.length > 1" class="pb-1 text-small text-muted">{{ supplementalPrompt }}</div>
                        <div
                            v-for="(row, idx) in eligibleSupplementalEarnings"
                            :key="idx"
                            class="d-flex justify-content-between align-items-center"
                            :class="{'py-1': eligibleSupplementalEarnings.length > 1}"
                        >
                            <span class="mr-3">{{ eligibleSupplementalEarnings.length === 1 ? supplementalPrompt : row.label }}</span>
                            <div class="flex-shrink-0 text-nowrap">
                                <label class="mb-0 mr-3">
                                    <input type="radio" :name="`supplemental-${idx}`" :value="true" v-model="row.earning.is_supplemental" /> Yes
                                </label>
                                <label class="mb-0">
                                    <input type="radio" :name="`supplemental-${idx}`" :value="false" v-model="row.earning.is_supplemental" /> No
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Step 2: Deductions & Taxes -->
                <div v-if="step === 'deductions-taxes'">
                    <div class="pb-2">Enter the deduction and tax settings for this earnings statement.</div>

                    <div v-if="hasDeductions" class="pt-2 pb-1"><strong>How will you handle deductions on this earnings statement?</strong></div>

                    <div v-if="hasDeductions">
                        <table class="table css-table widget-table no-border mt-2">
                            <thead>
                                <tr>
                                    <th><h5 class="mb-0">Deduction</h5></th>
                                    <th class="text-center"><h5 class="mb-0 clickable-header" @click="setAllDeductions('allow')">Allow</h5></th>
                                    <th class="text-center"><h5 class="mb-0 clickable-header" @click="setAllDeductions('block')">Block</h5></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="dc in employeeDeductions.deferred_comps" :key="`dc-${dc.id}`">
                                    <td>
                                        <span class="deduction-icon">
                                            <i v-show="formData.blocked_or_allowed_deductions.deferred_comps[dc.id] == 'block'" class="fas fa-fw fa-ban"></i>
                                            <i v-show="formData.blocked_or_allowed_deductions.deferred_comps[dc.id] == 'allow'" class="fas fa-fw fa-check"></i>
                                        </span>
                                        <span :class="{'text-muted': formData.blocked_or_allowed_deductions.deferred_comps[dc.id] == 'block'}">
                                            {{ dc.deferred_comp_plan.display_name.toUpperCase() }}
                                        </span>
                                    </td>
                                    <td class="text-center"><input type="radio" :name="`deferred_comps-${dc.id}`" value="allow" v-model="formData.blocked_or_allowed_deductions.deferred_comps[dc.id]"/></td>
                                    <td class="text-center"><input type="radio" :name="`deferred_comps-${dc.id}`" value="block" v-model="formData.blocked_or_allowed_deductions.deferred_comps[dc.id]"/></td>
                                </tr>
                                <tr v-for="md in employeeDeductions.misc_deductions" :key="`md-${md.id}`">
                                    <td>
                                        <span class="deduction-icon">
                                            <i v-show="formData.blocked_or_allowed_deductions.misc_deductions[md.id] == 'block'" class="fas fa-fw fa-ban"></i>
                                            <i v-show="formData.blocked_or_allowed_deductions.misc_deductions[md.id] == 'allow'" class="fas fa-fw fa-check"></i>
                                        </span>
                                        <span :class="{'text-muted': formData.blocked_or_allowed_deductions.misc_deductions[md.id] == 'block'}">
                                            {{ md.company_deduction_type.name.toUpperCase() }}
                                        </span>
                                    </td>
                                    <td class="text-center"><input type="radio" :name="`misc_deductions-${md.id}`" value="allow" v-model="formData.blocked_or_allowed_deductions.misc_deductions[md.id]"/></td>
                                    <td class="text-center"><input type="radio" :name="`misc_deductions-${md.id}`" value="block" v-model="formData.blocked_or_allowed_deductions.misc_deductions[md.id]"/></td>
                                </tr>
                                <tr v-for="ip in employeeDeductions.insurance_plans" :key="`ip-${ip.id}`">
                                    <td>
                                        <span class="deduction-icon">
                                            <i v-show="formData.blocked_or_allowed_deductions.insurance_plans[ip.id] == 'block'" class="fas fa-fw fa-ban"></i>
                                            <i v-show="formData.blocked_or_allowed_deductions.insurance_plans[ip.id] == 'allow'" class="fas fa-fw fa-check"></i>
                                        </span>
                                        <span :class="{'text-muted': formData.blocked_or_allowed_deductions.insurance_plans[ip.id] == 'block'}">
                                            {{ ip.insurance_plan.plan_name.toUpperCase() }}
                                        </span>
                                    </td>
                                    <td class="text-center"><input type="radio" :name="`insurance_plans-${ip.id}`" value="allow" v-model="formData.blocked_or_allowed_deductions.insurance_plans[ip.id]"/></td>
                                    <td class="text-center"><input type="radio" :name="`insurance_plans-${ip.id}`" value="block" v-model="formData.blocked_or_allowed_deductions.insurance_plans[ip.id]"/></td>
                                </tr>
                                <tr v-for="iwo in employeeDeductions.iwos" :key="`iwo-${iwo.id}`">
                                    <td>
                                        <span class="deduction-icon">
                                            <i v-show="formData.blocked_or_allowed_deductions.iwos[iwo.id] == 'block'" class="fas fa-fw fa-ban"></i>
                                            <i v-show="formData.blocked_or_allowed_deductions.iwos[iwo.id] == 'allow'" class="fas fa-fw fa-check"></i>
                                        </span>
                                        <span :class="{'text-muted': formData.blocked_or_allowed_deductions.iwos[iwo.id] == 'block'}">
                                            {{ iwo.iwo_type.name.toUpperCase() }}
                                        </span>
                                    </td>
                                    <td class="text-center"><input type="radio" :name="`iwos-${iwo.id}`" value="allow" v-model="formData.blocked_or_allowed_deductions.iwos[iwo.id]"/></td>
                                    <td class="text-center"><input type="radio" :name="`iwos-${iwo.id}`" value="block" v-model="formData.blocked_or_allowed_deductions.iwos[iwo.id]"/></td>
                                </tr>
                                <tr v-for="obd in employeeDeductions.other_benefit_deductions" :key="`obd-${obd.id}`">
                                    <td>
                                        <span class="deduction-icon">
                                            <i v-show="formData.blocked_or_allowed_deductions.other_benefit_deductions[obd.id] == 'block'" class="fas fa-fw fa-ban"></i>
                                            <i v-show="formData.blocked_or_allowed_deductions.other_benefit_deductions[obd.id] == 'allow'" class="fas fa-fw fa-check"></i>
                                        </span>
                                        <span :class="{'text-muted': formData.blocked_or_allowed_deductions.other_benefit_deductions[obd.id] == 'block'}">
                                            {{ obd.company_deduction_type.name.toUpperCase() }}
                                        </span>
                                    </td>
                                    <td class="text-center"><input type="radio" :name="`other_benefit_deductions-${obd.id}`" value="allow" v-model="formData.blocked_or_allowed_deductions.other_benefit_deductions[obd.id]"/></td>
                                    <td class="text-center"><input type="radio" :name="`other_benefit_deductions-${obd.id}`" value="block" v-model="formData.blocked_or_allowed_deductions.other_benefit_deductions[obd.id]"/></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="employeeTaxes.length" class="pt-3 pb-1"><strong>How will you handle taxes on this earnings statement?</strong></div>

                    <div v-if="employeeTaxes.length">
                        <table class="table css-table widget-table no-border mt-2">
                            <thead>
                                <tr>
                                    <th><h5 class="mb-0">Tax</h5></th>
                                    <th class="text-center"><h5 class="mb-0 clickable-header" @click="setAllTaxes('allow')">Allow</h5></th>
                                    <th class="text-center"><h5 class="mb-0 clickable-header" @click="setAllTaxes('block')">Block</h5></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="tax in employeeTaxes" :key="`tax-${tax.unique_tax_id}`">
                                    <td>
                                        <span class="deduction-icon">
                                            <i v-show="formData.blocked_or_allowed_taxes[tax.unique_tax_id] == 'block'" class="fas fa-fw fa-ban"></i>
                                            <i v-show="formData.blocked_or_allowed_taxes[tax.unique_tax_id] == 'allow'" class="fas fa-fw fa-check"></i>
                                        </span>
                                        <span :class="{'text-muted': formData.blocked_or_allowed_taxes[tax.unique_tax_id] == 'block'}">
                                            {{ tax.description.toUpperCase() }}
                                        </span>
                                    </td>
                                    <td class="text-center"><input type="radio" :name="`tax-${tax.unique_tax_id}`" value="allow" v-model="formData.blocked_or_allowed_taxes[tax.unique_tax_id]"/></td>
                                    <td class="text-center"><input type="radio" :name="`tax-${tax.unique_tax_id}`" value="block" v-model="formData.blocked_or_allowed_taxes[tax.unique_tax_id]"/></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <form-input label="Tax Frequency Settings" v-model="formData.pay_schedule" type="select" :options="taxFrequencyOptions" :noblank="true" class="pt-3"/>
                    <div class="text-small text-muted select-help">Which tax rate will you use for this check?</div>
                </div>

                <!-- Step 3: Earnings Statement (read-only preview of unsaved edits) -->
                <div v-if="step === 'earnings-statement' && statementResult && statement">
                    <earnings-statement-view
                        :client="client"
                        :employee="employee"
                        :statement="statement"
                        :result="statementResult"
                        :paychecks="statementPaychecks"
                        :earning-types="earningTypes"
                        :company-earning-types="clientEarningTypes"
                        :tax-configs="employee.sym_tax_config"
                        :blocked-deductions="blockedDeductionsByCategory"
                        :blocked-taxes="blockedTaxes"
                    />
                </div>
            </template>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>
            <button-row stacked>
                <button v-if="step === steps[0]" type="button" class="btn btn-outline-primary" @click.prevent="$emit('close')">Cancel</button>
                <button v-else type="button" class="btn btn-outline-primary" @click.prevent="back">Back</button>
                <button v-if="step === 'earnings-statement'" type="button" class="btn btn-primary" @click.prevent="save">Save</button>
                <button v-else type="button" class="btn btn-primary" @click.prevent="next">Continue</button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import CellInput from '@/components/FastTable/CellInput'
import EarningsStatementView from './EarningsStatementView'

const DEDUCTION_CATEGORIES = ['deferred_comps', 'misc_deductions', 'insurance_plans', 'iwos', 'other_benefit_deductions']

export default {
    // flow selects which steps to show: 'earnings' (earnings + preview) or 'taxes-deductions'
    // (deductions/taxes + preview). Both flows share this component and the same save.
    // createSeparateCheck switches the component into the separate-check creation flow.
    props: ['client', 'employeeId', 'earningsStatementId', 'earningTypes', 'clientEarningTypes', 'flow', 'createSeparateCheck', 'runId'],
    mixins: [FormMixin, ModalMixin],
    components: {CellInput, EarningsStatementView},
    emits: ['save', 'close'],
    data() {
        return {
            dataLoaded: false,
            // The separate-check flow opens on its check-options step; the edit flows open on
            // whichever single edit step their flow selects.
            step: this.createSeparateCheck
                ? 'check-options'
                : (this.flow === 'taxes-deductions' ? 'deductions-taxes' : 'earnings'),
            localPaychecks: [],
            // True when the statement being edited is a separate check (set after loadStatement);
            // the edit flow then shows the check-options step too.
            isSeparateCheckStatement: false,
            // Separate-check creation only: the company positions to choose from and the chosen one
            positionOptions: [],
            selectedPositionId: null,
            employee: null,
            employeeDeductions: {
                deferred_comps: [],
                misc_deductions: [],
                insurance_plans: [],
                iwos: [],
                other_benefit_deductions: [],
            },
            employeeTaxes: [],
            payScheduleOptions: [],
            // Labels the "regular schedule" option.
            defaultPaySchedule: '',
            formData: {
                // Blank means the paygroup's regular schedule.
                pay_schedule: '',
                // Separate-check setup (check-options step). entry_type maps directly to the model
                // field; check_delivery is converted to the send_as_paper_check bool when saving.
                entry_type: 'gross',
                check_delivery: 'direct_deposit',
                blocked_or_allowed_deductions: {
                    deferred_comps: {},
                    misc_deductions: {},
                    insurance_plans: {},
                    iwos: {},
                    other_benefit_deductions: {},
                },
                blocked_or_allowed_taxes: {},
            },
            loadedBlockedDeductions: null,
            loadedBlockedTaxes: null,
            // Earnings-statement preview (computed temp, never persisted until Save)
            statementResult: null,
            statementPaychecks: [],
            statement: null,
        }
    },
    mounted() {
        this.getData()
    },
    computed: {
        steps() {
            // The preview ('earnings-statement') is always the final step. The separate-check
            // creation flow walks check-options -> earnings -> deductions/taxes -> preview; the edit
            // flows show a single edit step (plus the preview).
            if (this.createSeparateCheck) return ['check-options', 'earnings', 'deductions-taxes', 'earnings-statement']
            if (this.flow === 'taxes-deductions') return ['deductions-taxes', 'earnings-statement']
            // Modify Earnings (earnings flow): editing an existing separate check also exposes its
            // entry-type / delivery options as the first step.
            return this.isSeparateCheckStatement
                ? ['check-options', 'earnings', 'earnings-statement']
                : ['earnings', 'earnings-statement']
        },
        entryTypeOptions() {
            return [
                { value: 'gross', text: 'Gross Amount' },
                { value: 'net', text: 'Net Amount (Gross-up Check)' },
            ]
        },
        checkDeliveryOptions() {
            return [
                { value: 'direct_deposit', text: 'Direct Deposit' },
                { value: 'paper_check', text: 'Paper Check' },
            ]
        },
        positionSelectOptions() {
            return this.positionOptions.map(position => ({
                value: position.id,
                text: `${position.work_location.name} / ${position.slug}`,
            }))
        },
        selectedPosition() {
            if (!this.createSeparateCheck || this.selectedPositionId == null) return null
            return this.positionOptions.find(position => String(position.id) === String(this.selectedPositionId)) || null
        },
        companyEarningTypeByCompanyAndSlug() {
            // Keyed by `${company_id}:${global slug}`: the type an earning without its own
            // company_earning_type_id resolves to. A company can have several under one global
            // type, so this takes the lowest id, as the backend does.
            const map = {}
            ;[...(this.clientEarningTypes || [])].sort((a, b) => a.id - b.id).forEach(cet => {
                const key = `${cet.company_id}:${cet.earning_type.slug}`
                if (!map[key]) map[key] = cet
            })
            return map
        },
        companyEarningTypeById() {
            return (this.clientEarningTypes || []).reduce((acc, cet) => {
                acc[cet.id] = cet
                return acc
            }, {})
        },
        taxFrequencyOptions() {
            // The first option (blank value) means the paygroup's regular schedule; the
            // rest set an explicit override.
            const defaultOption = this.payScheduleOptions.find(option => option.value === this.defaultPaySchedule)
            const defaultLabel = defaultOption ? defaultOption.text : this.defaultPaySchedule
            return [
                { value: '', text: `${defaultLabel} (regular pay schedule)` },
                ...this.payScheduleOptions,
            ]
        },
        eligibleSupplementalEarnings() {
            // All supplemental-eligible earnings as one list; each row keeps a reference
            // to its earning and a position-prefixed label.
            const rows = []
            this.localPaychecks.forEach(paycheck => {
                (paycheck.earnings || []).forEach(earning => {
                    if (!this.earningSupplementalEligible(paycheck, earning)) return
                    const label = `${paycheck.work_location.name} / ${earning.position_slug} — ${earning.earning_type_name}`
                    rows.push({ earning: earning, label: label })
                })
            })
            return rows
        },
        supplementalPrompt() {
            // Name the single eligible earning so it's visible without expanding a list.
            const eligible = this.eligibleSupplementalEarnings
            if (eligible.length === 1) {
                return `Should the supplemental rate apply to the ${eligible[0].earning.earning_type_name} earning?`
            }
            return 'Should the supplemental rate apply to any of these earnings?'
        },
        hasDeductions() {
            return DEDUCTION_CATEGORIES.some(category => (this.employeeDeductions[category] || []).length)
        },
        blockedDeductionsByCategory() {
            // Resolve the blocked deduction ids per category from the per-item allow/block selections
            const result = {}
            DEDUCTION_CATEGORIES.forEach(category => {
                const items = this.employeeDeductions[category] || []
                result[category] = items
                    .filter(item => this.formData.blocked_or_allowed_deductions[category][item.id] === 'block')
                    .map(item => item.id)
            })
            return result
        },
        blockedTaxes() {
            // Resolve the blocked unique_tax_ids from the per-item allow/block selections
            return this.employeeTaxes
                .filter(tax => this.formData.blocked_or_allowed_taxes[tax.unique_tax_id] === 'block')
                .map(tax => tax.unique_tax_id)
        },
    },
    methods: {
        getData() {
            this.$store.dispatch('START_LOADING')
            const promises = [
                this.getEmployee(),
                this.getEmployeeDeductions(),
                this.getEmployeeTaxes(),
            ]
            if (this.createSeparateCheck) {
                // No statement exists yet: load the positions to add a check on and the setup data.
                promises.push(this.getPositionOptions(), this.loadSeparateCheckSetup())
            } else {
                promises.push(this.loadStatement())
            }
            Promise.all(promises).then(() => {
                this.initBlockedOrAllowedDeductions()
                this.initBlockedOrAllowedTaxes()
                // Open on the first step now that the flow is known (a separate-check edit only
                // learns it has the check-options step once the statement has loaded).
                this.step = this.steps[0]
                this.dataLoaded = true
                this.$store.dispatch('STOP_LOADING')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                    return
                }
                this.$bus.showError('Could not load earnings data. Please refresh the page and try again.')
            })
        },
        getEmployee() {
            return this.$api.get(`/clients/${this.client.id}/payroll/employees/${this.employeeId}`).then(resp => {
                this.employee = resp
            })
        },
        getEmployeeDeductions() {
            return this.$api.get(`/clients/${this.client.id}/payroll/employees/${this.employeeId}/deductions`).then(resp => {
                this.employeeDeductions = resp
            })
        },
        getEmployeeTaxes() {
            return this.$api.get(`/clients/${this.client.id}/payroll/employees/${this.employeeId}/taxes`).then(resp => {
                this.employeeTaxes = resp['taxes']
            })
        },
        loadStatement() {
            return this.$api.get(`/clients/${this.client.id}/payroll/paygrid/earnings-statements/${this.earningsStatementId}/base-paychecks`).then(resp => {
                this.localPaychecks = JSON.parse(JSON.stringify(resp['paychecks'] || []))
                // Normalize is_supplemental to a strict boolean so the Yes/No toggle reflects a
                // definite state (defaulting to No) even when a stored earning omits the flag.
                this.localPaychecks.forEach(paycheck => {
                    (paycheck.earnings || []).forEach(earning => {
                        earning.is_supplemental = Boolean(earning.is_supplemental)
                    })
                })
                this.loadedBlockedDeductions = resp['blocked_deductions']
                this.loadedBlockedTaxes = resp['blocked_taxes']
                this.payScheduleOptions = resp['pay_schedule_options']
                this.defaultPaySchedule = resp['default_pay_schedule']
                this.formData.pay_schedule = resp['pay_schedule'] || ''
                this.isSeparateCheckStatement = Boolean(resp['is_separate_check'])
                this.formData.entry_type = resp['entry_type'] || 'gross'
                this.formData.check_delivery = resp['send_as_paper_check'] ? 'paper_check' : 'direct_deposit'
            })
        },
        getPositionOptions() {
            return this.$api.get(`/clients/${this.client.id}/payroll/employees/${this.employeeId}/position-options`).then(resp => {
                this.positionOptions = resp
            })
        },
        loadSeparateCheckSetup() {
            return this.$api.get(`/clients/${this.client.id}/payroll/runs/${this.runId}/employees/${this.employeeId}/separate-check`).then(resp => {
                this.payScheduleOptions = resp['pay_schedule_options']
                this.defaultPaySchedule = resp['default_pay_schedule']
                this.formData.pay_schedule = resp['pay_schedule'] || ''
            })
        },
        positionGroups(paycheck) {
            // For a separate check the chosen position is shown even before any earnings are added
            // (so the "+ Add Earning" button appears); otherwise group the paycheck's earnings.
            if (this.createSeparateCheck) {
                if (!this.selectedPosition) return []
                return [{ slug: this.selectedPosition.slug, earnings: paycheck.earnings }]
            }
            const byPositionSlug = this.earningsByPositionSlug(paycheck)
            return Object.keys(byPositionSlug).map(slug => ({ slug: slug, earnings: byPositionSlug[slug] }))
        },
        selectPosition(positionId) {
            this.selectedPositionId = positionId
            const position = this.positionOptions.find(p => String(p.id) === String(positionId))
            if (!position) {
                this.localPaychecks = []
                return
            }
            // One unsaved paycheck for the chosen position; seed a single blank earning to fill in.
            this.localPaychecks = [{
                id: null,
                company_id: this.employee.company.id,
                work_location: position.work_location,
                work_location_id: position.work_location.id,
                employee_id: this.employeeId,
                is_separate_check: true,
                earnings: [],
            }]
            this.addCustomEarning(this.localPaychecks[0], position.slug)
        },
        initBlockedOrAllowedDeductions() {
            // Seed each deduction's allow/block radio from the saved blocked deductions
            const blocked = this.loadedBlockedDeductions || {}
            const map = {
                deferred_comps: {},
                misc_deductions: {},
                insurance_plans: {},
                iwos: {},
                other_benefit_deductions: {},
            }
            DEDUCTION_CATEGORIES.forEach(category => {
                const blockedIds = (blocked[category] || []).map(id => String(id))
                ;(this.employeeDeductions[category] || []).forEach(item => {
                    map[category][item.id] = blockedIds.includes(String(item.id)) ? 'block' : 'allow'
                })
            })
            this.formData.blocked_or_allowed_deductions = map
        },
        initBlockedOrAllowedTaxes() {
            // Seed each tax's allow/block radio from the saved blocked taxes
            const blocked = (this.loadedBlockedTaxes || []).map(id => String(id))
            const map = {}
            this.employeeTaxes.forEach(tax => {
                map[tax.unique_tax_id] = blocked.includes(String(tax.unique_tax_id)) ? 'block' : 'allow'
            })
            this.formData.blocked_or_allowed_taxes = map
        },
        setAllDeductions(value) {
            // Column-header shortcut: set every deduction's allow/block radio across all categories
            DEDUCTION_CATEGORIES.forEach(category => {
                const map = this.formData.blocked_or_allowed_deductions[category]
                Object.keys(map).forEach(id => {
                    map[id] = value
                })
            })
        },
        setAllTaxes(value) {
            // Column-header shortcut: set every tax's allow/block radio
            const map = this.formData.blocked_or_allowed_taxes
            Object.keys(map).forEach(id => {
                map[id] = value
            })
        },
        earningsByPaycheckId() {
            const map = {}
            this.localPaychecks.forEach(paycheck => {
                map[paycheck.id] = paycheck.earnings
            })
            return map
        },
        earningSupplementalEligible(paycheck, earning) {
            // The supplemental rate is only offered when the earning's CompanyEarningType is
            // flagged for supplemental treatment
            const cet = this.companyEarningTypeFor(paycheck, earning)
            return !!(cet && cet.symmetry_wage_type_supplemental_treatment)
        },
        earningsByPositionSlug(paycheck) {
            return (paycheck.earnings || []).reduce((acc, earning) => {
                const slug = earning.position_slug
                if (!acc[slug]) acc[slug] = []
                acc[slug].push(earning)
                return acc
            }, {})
        },
        // The earning's company earning type: its own if it has one, otherwise what its global type
        // resolves to for the company (earnings saved before they carried their company type)
        companyEarningTypeFor(paycheck, earning) {
            return this.companyEarningTypeById[earning.company_earning_type_id]
                || this.companyEarningTypeByCompanyAndSlug[`${paycheck.company_id}:${earning.earning_type_slug}`]
        },
        earningCompanyTypeValue(paycheck, earning) {
            if (!earning.earning_type_slug) return ''
            const cet = this.companyEarningTypeFor(paycheck, earning)
            return cet ? String(cet.id) : ''
        },
        customEarningTypeOptions(companyId) {
            if (!this.clientEarningTypes?.length) return []
            // One option per company earning type, under the company's own name: several can share a
            // global type (BONUS and DELIVERY RUNS are both OTHER). Overtime is entered in an
            // earning's Overtime column, not added as its own earning.
            return this.clientEarningTypes
                .filter(cet => cet.company_id == companyId && cet.earning_type.slug !== 'overtime')
                .map(cet => ({ text: cet.name, value: String(cet.id) }))
                .sort((a, b) => a.text.localeCompare(b.text))
        },
        onCustomEarningTypeChange(earning, companyEarningTypeId) {
            const cet = this.companyEarningTypeById[companyEarningTypeId]
            if (cet) {
                earning.company_earning_type_id = cet.id
                earning.earning_type_slug = cet.earning_type.slug
                earning.earning_type_name = cet.name
                // The company's own type: it can differ from the global type's (BONUS is an amount
                // under OTHER, which may be hourly)
                earning.earning_type_type = cet.type
            } else {
                earning.company_earning_type_id = null
                earning.earning_type_slug = ''
                earning.earning_type_name = ''
                earning.earning_type_type = ''
            }
            // Reset input values so stale numbers from a previous type don't persist
            earning.hours = ''
            earning.units = ''
            earning.overtime_hours = ''
            earning.amount = ''
            earning.is_amount_entered = false
            // Clear supplemental flag in case the new type isn't eligible for it
            earning.is_supplemental = false
        },
        onCellBlur(earning, field, value) {
            // CellInput emits the value already reformatted to 2 decimals (matching the main paygrid),
            // so we commit it to the earning before recalculating
            const changed = !this.sameNumericValue(earning[field], value)
            earning[field] = value
            // The amount is recalculated on every save, so mark one that was typed in (see PayGridTable)
            if (field === 'amount') {
                earning.is_amount_entered = value !== ''
            }
            // Recompute (but do NOT persist) so the amount column stays in sync
            if (changed) this.calculateTemp()
        },
        hasEnteredValue(value) {
            return value !== '' && value != null
        },
        sameNumericValue(a, b) {
            // Treat blank/null/0 as equivalent (the backend coerces them all to 0), and compare
            // numerically so "8" and "8.00" aren't seen as different
            const toNumber = (value) => (value === '' || value == null) ? 0 : Number(value)
            return toNumber(a) === toNumber(b)
        },
        calculateTemp() {
            if (this.createSeparateCheck) return this.calculateTempSeparate()
            // Recompute amounts across the group WITHOUT saving (nothing persists until Save)
            const url = `/clients/${this.client.id}/payroll/paygrid/earnings-statements/${this.earningsStatementId}/base-paychecks`
            this.$api.post(url, { action: 'calculate_temp', earnings_by_paycheck_id: this.earningsByPaycheckId() }).then(resp => {
                const savedById = {}
                resp['paychecks'].forEach(paycheck => {
                    savedById[paycheck.id] = paycheck.earnings
                })
                // Only sync the engine-calculated amounts back; leave the user's other in-progress edits alone
                this.localPaychecks.forEach(paycheck => {
                    const savedEarnings = savedById[paycheck.id]
                    if (!savedEarnings) return
                    paycheck.earnings.forEach((earning, idx) => {
                        if (savedEarnings[idx]) earning.amount = savedEarnings[idx].amount
                    })
                })
            }).catch(errors => {
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                    return
                }
                this.$bus.showError(errors.__all__)
            })
        },
        getTempEarningsStatement() {
            if (this.createSeparateCheck) return this.getTempEarningsStatementSeparate()
            this.$store.dispatch('START_LOADING')
            const url = `/clients/${this.client.id}/payroll/paygrid/earnings-statements/${this.earningsStatementId}/net-pay`
            this.$api.post(url, {
                action: 'calculate_temp',
                earnings_by_paycheck_id: this.earningsByPaycheckId(),
                blocked_deductions: this.blockedDeductionsByCategory,
                blocked_taxes: this.blockedTaxes,
                pay_schedule: this.formData.pay_schedule,
            }).then(resp => {
                this.statementPaychecks = resp['paychecks']
                this.statement = resp['earnings_statement']
                this.statementResult = resp['earnings_statement_result']
                this.$store.dispatch('STOP_LOADING')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                    return
                }
                this.$bus.showError(errors.__all__)
            })
        },
        addCustomEarning(paycheck, positionSlug) {
            // Copy position identifiers from an existing earning in this position group (preferring a
            // regular one), otherwise (the separate-check creation case, where the position starts
            // empty) from the chosen position itself. Any earning in the group carries the same
            // position/job identifiers, so falling back to the first keeps editing flows — where no
            // regular earning may exist — from leaving position_id null.
            const groupEarnings = paycheck.earnings.filter(e => e.position_slug === positionSlug)
            const copyFrom = groupEarnings.find(e => e.earning_type_slug === 'regular') || groupEarnings[0]
            const position = this.createSeparateCheck ? this.selectedPosition : null
            paycheck.earnings.push({
                is_custom: true,
                position_slug: positionSlug,
                company_earning_type_id: null,
                earning_type_slug: '',
                earning_type_name: '',
                earning_type_type: '',
                rate: '',
                hours: '',
                units: '',
                overtime_hours: '',
                amount: '',
                is_supplemental: false,
                org_level_1_value: copyFrom?.org_level_1_value || position?.org_level_1_value || '',
                org_level_2_value: copyFrom?.org_level_2_value || position?.org_level_2_value || '',
                org_level_3_value: copyFrom?.org_level_3_value || position?.org_level_3_value || '',
                org_level_4_value: copyFrom?.org_level_4_value || position?.org_level_4_value || '',
                org_level_5_value: copyFrom?.org_level_5_value || position?.org_level_5_value || '',
                job_id: copyFrom?.job_id || null,
                position_id: copyFrom?.position_id || position?.id || null,
                employee_id: copyFrom?.employee_id || this.employeeId || null,
            })
        },
        removeEarning(paycheck, earning) {
            const idx = paycheck.earnings.indexOf(earning)
            if (idx !== -1) paycheck.earnings.splice(idx, 1)
            this.calculateTemp()
        },
        next() {
            // Separate-check flow validates the position/earnings before leaving the earnings step
            if (this.step === 'earnings' && this.createSeparateCheck && !this.canContinueSeparate()) return
            const nextStep = this.steps[this.steps.indexOf(this.step) + 1]
            if (!nextStep) return
            this.step = nextStep
            if (nextStep === 'earnings-statement') {
                this.getTempEarningsStatement()
            }
        },
        back() {
            const prevStep = this.steps[this.steps.indexOf(this.step) - 1]
            if (prevStep) this.step = prevStep
        },
        save() {
            if (this.createSeparateCheck) return this.saveSeparate()
            this.$store.dispatch('START_LOADING')
            const url = `/clients/${this.client.id}/payroll/paygrid/earnings-statements/${this.earningsStatementId}/base-paychecks`
            const payload = {
                action: 'save',
                earnings_by_paycheck_id: this.earningsByPaycheckId(),
                blocked_deductions: this.blockedDeductionsByCategory,
                blocked_taxes: this.blockedTaxes,
                pay_schedule: this.formData.pay_schedule,
            }
            // Only a separate-check statement carries entry-type / delivery; the backend leaves the
            // fields untouched when these keys are absent.
            if (this.isSeparateCheckStatement) {
                payload.entry_type = this.formData.entry_type
                payload.send_as_paper_check = this.formData.check_delivery === 'paper_check'
            }
            this.$api.post(url, payload).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('save', resp['paychecks'])
                this.$emit('close')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                    return
                }
                this.$bus.showError(errors.__all__)
            })
        },
        // --- Separate-check creation (no statement exists yet; keyed by run + employee) ---
        separateCheckUrl() {
            return `/clients/${this.client.id}/payroll/runs/${this.runId}/employees/${this.employeeId}/separate-check`
        },
        canContinueSeparate() {
            if (!this.selectedPositionId) {
                this.$bus.showError('Please select a position.')
                return false
            }
            const earnings = this.localPaychecks[0]?.earnings || []
            const hasEarning = earnings.some(earning => earning.earning_type_slug)
            if (!hasEarning) {
                this.$bus.showError('Please add at least one earning.')
                return false
            }
            return true
        },
        calculateTempSeparate() {
            if (!this.localPaychecks.length) return
            this.$api.post(this.separateCheckUrl(), {
                action: 'calculate_temp',
                position_id: this.selectedPositionId,
                earnings: this.localPaychecks[0].earnings,
            }).then(resp => {
                const recomputed = resp['earnings'] || []
                this.localPaychecks[0].earnings.forEach((earning, idx) => {
                    if (recomputed[idx]) earning.amount = recomputed[idx].amount
                })
            }).catch(errors => {
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                    return
                }
                this.$bus.showError(errors.__all__)
            })
        },
        getTempEarningsStatementSeparate() {
            this.$store.dispatch('START_LOADING')
            this.$api.post(this.separateCheckUrl(), {
                action: 'calculate_net',
                position_id: this.selectedPositionId,
                earnings: this.localPaychecks[0].earnings,
                blocked_deductions: this.blockedDeductionsByCategory,
                blocked_taxes: this.blockedTaxes,
                pay_schedule: this.formData.pay_schedule,
            }).then(resp => {
                this.statementPaychecks = resp['paychecks']
                this.statement = resp['earnings_statement']
                this.statementResult = resp['earnings_statement_result']
                this.$store.dispatch('STOP_LOADING')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                    return
                }
                this.$bus.showError(errors.__all__)
            })
        },
        saveSeparate() {
            this.$store.dispatch('START_LOADING')
            this.$api.post(this.separateCheckUrl(), {
                action: 'save',
                position_id: this.selectedPositionId,
                earnings: this.localPaychecks[0].earnings,
                entry_type: this.formData.entry_type,
                send_as_paper_check: this.formData.check_delivery === 'paper_check',
                blocked_deductions: this.blockedDeductionsByCategory,
                blocked_taxes: this.blockedTaxes,
                pay_schedule: this.formData.pay_schedule,
            }).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('save', resp)
                this.$emit('close')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                    return
                }
                this.$bus.showError(errors.__all__)
            })
        },
    },
}
</script>

<style lang="scss">
.edit-mode {
    th.earning-description {
        width: 200px;
    }
    .field-prefix {
        text-align: left;
    }
}
.custom-earning-select {
    .select2-control {
        padding-top: 0px;
        padding-right: 10px !important;
        margin-bottom: 0px;

        .field-postfix {
            top: 0px;
        }
    }
}
.select-help {
    margin-top: -12px;
}
.table.no-border td {
    border-top: none;
}
.fa-ban, .fa-check {
    color: #206067;
}
.deduction-icon {
    display: inline-flex;
    width: 1rem;
    margin-left: -1.25rem;
    margin-right: 0.25rem;
}
.clickable-header {
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
}
</style>
